import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../../services/api'
import useAuth from '../../hooks/useAuth'
import ProductEditDialog from './ProductEditDialog'
import { canEditProduct } from './productPermissions'
import PrivatePageSection from '../../components/layout/PrivatePageSection'
import {
  normalizeArray,
  pickValue,
  readByKeys,
  toSafeText,
} from '../../utils/defensiveData'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material'

const hasProductInUserRelations = (user, targetProductId) => {
  if (!user || targetProductId === null || targetProductId === undefined) {
    return false
  }

  const directProductId = readByKeys(user, ['product_id', 'productId'], null)
  if (directProductId !== null && String(directProductId) === String(targetProductId)) {
    return true
  }

  const relatedProducts = normalizeArray(
    readByKeys(user, ['products', 'assignedProducts', 'productList'], [])
  )

  return relatedProducts.some((entry) => {
    if (entry && typeof entry === 'object') {
      const entryId = readByKeys(entry, ['id', 'product_id', 'productId'], null)
      return entryId !== null && String(entryId) === String(targetProductId)
    }

    return String(entry) === String(targetProductId)
  })
}

const ProductDetail = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canReadUsers = user?.isGlobalAdmin === true || (user?.roles || []).some((role) => (typeof role === 'string' ? role : role?.type) === 'globalAdmin')
  const { productId } = useParams()
  const [product, setProduct] = useState(null)
  const [loadedContext, setLoadedContext] = useState(null)
  const [editing, setEditing] = useState(false)
  const [revision, setRevision] = useState(0)
  const [client, setClient] = useState(null)
  const [linkedUsers, setLinkedUsers] = useState([])
  const [usersAvailable, setUsersAvailable] = useState(false)
  const [consumersCount, setConsumersCount] = useState(null)
  const [activitiesCount, setActivitiesCount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    const fetchProductDetail = async () => {
      setEditing(false)
      setLoadedContext(null)
      setLoading(true)
      setError('')

      try {
        const productResponse = await api.get(`/products/${productId}`)
        const productPayload = productResponse?.data || null
        if (!active) return
        setProduct(productPayload)
        setLoadedContext({ user, clientId: user?.clientId, productId, revision })

        const resolvedClientId = pickValue(
          productPayload,
          'client_id',
          'clientId',
          null
        )

        const [clientResult, usersResult, consumersResult, activitiesResult] =
          await Promise.allSettled([
            resolvedClientId
              ? api.get(`/clients/${resolvedClientId}`)
              : Promise.resolve(null),
            canReadUsers ? api.get('/users') : Promise.reject(new Error('Unavailable')),
            api.get(`/products/${productId}/consumers`),
            api.get('/activities', {
              params: { productId, page: 1, limit: 5 },
            }),
          ])

        if (!active) return
        setUsersAvailable(usersResult.status === 'fulfilled')
        if (clientResult.status === 'fulfilled') {
          setClient(clientResult.value?.data || null)
        } else {
          setClient(null)
        }

        if (usersResult.status === 'fulfilled') {
          const usersCatalog = normalizeArray(usersResult.value?.data)
          const associated = usersCatalog.filter((userEntry) =>
            hasProductInUserRelations(userEntry, productId)
          )
          setLinkedUsers(associated)
        } else {
          setLinkedUsers([])
        }

        if (consumersResult.status === 'fulfilled') {
          const consumers = normalizeArray(consumersResult.value?.data)
          setConsumersCount(consumers.length)
        } else {
          setConsumersCount(null)
        }

        if (activitiesResult.status === 'fulfilled') {
          const activities = normalizeArray(activitiesResult.value?.data)
          setActivitiesCount(activities.length)
        } else {
          setActivitiesCount(null)
        }
      } catch (_fetchError) {
        if (!active) return
        setProduct(null)
        setClient(null)
        setLinkedUsers([])
        setConsumersCount(null)
        setActivitiesCount(null)
        setError('No fue posible cargar el detalle del producto.')
      } finally {
        if (active) setLoading(false)
      }
    }

    if (!/^\d+$/.test(productId || '') || !Number.isSafeInteger(Number(productId)) || Number(productId) <= 0) {
      setError('No se encontró un identificador de producto válido.')
      setLoading(false)
      return
    }

    fetchProductDetail()
    return () => { active = false }
  }, [productId, user, user?.clientId, canReadUsers, revision])

  const detailIsCurrent = loadedContext?.user === user &&
    loadedContext?.clientId === user?.clientId && loadedContext?.productId === productId &&
    loadedContext?.revision === revision
  const canEdit = detailIsCurrent && String(product?.id) === String(productId) && canEditProduct(user, product)

  const productName = useMemo(
    () =>
      toSafeText(
        readByKeys(product, ['name_product', 'nameProduct', 'name'], '(Sin nombre)'),
        '(Sin nombre)'
      ),
    [product]
  )

  const productDescription = useMemo(
    () =>
      toSafeText(
        readByKeys(product, ['description', 'descriptionLabel'], 'Sin descripción operativa'),
        'Sin descripción operativa'
      ),
    [product]
  )

  const productStatus = useMemo(
    () =>
      toSafeText(
        readByKeys(product, ['status', 'state', 'isActive'], 'No especificado'),
        'No especificado'
      ),
    [product]
  )

  const clientLabel = useMemo(() => {
    if (!client) return 'No disponible'

    return toSafeText(
      readByKeys(client, ['full_name_or_company', 'fullNameOrCompany', 'name'], 'No disponible'),
      'No disponible'
    )
  }, [client])

  if (loading || (!detailIsCurrent && !error)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !product) {
    return (
      <PrivatePageSection title="Detalle de Producto">
        <Stack spacing={2}>
          <Alert severity="error">{error || 'Producto no encontrado.'}</Alert>
          <Box>
            <Button variant="outlined" onClick={() => navigate('/products')}>
              Volver a productos
            </Button>
          </Box>
        </Stack>
      </PrivatePageSection>
    )
  }

  return (
    <PrivatePageSection
      title={productName}
      subtitle="Ficha operativa de producto"
      actions={
        <Stack direction="row" spacing={1}>
          {canEdit && <Button variant="outlined" onClick={() => setEditing(true)}>Editar</Button>}
          <Button variant="outlined" onClick={() => navigate('/products')}>
            Volver a productos
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/products/${productId}/consumers`)}
          >
            Ver consumidores
          </Button>
        </Stack>
      }
    >
      {editing && canEdit && <ProductEditDialog product={product} productId={productId}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); setRevision((value) => value + 1) }} />}
      <Stack spacing={2.5}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {productName}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={`ID: ${toSafeText(readByKeys(product, ['id'], '—'), '—')}`}
                />
                <Chip size="small" variant="outlined" label={`Estado: ${productStatus}`} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Datos generales
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2">Descripción: {productDescription}</Typography>
                <Typography variant="body2">Precio: {toSafeText(product.price, 'No especificado')}</Typography>
                <Typography variant="body2">
                  Cliente ID: {toSafeText(pickValue(product, 'client_id', 'clientId', 'No disponible'), 'No disponible')}
                </Typography>
                <Typography variant="body2">
                  Referencia operativa: {toSafeText(readByKeys(product, ['type', 'category', 'segment'], 'No especificado'), 'No especificado')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Cliente asociado
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2">Nombre: {clientLabel}</Typography>
                <Typography variant="body2">
                  Email: {toSafeText(readByKeys(client, ['email'], 'No disponible'), 'No disponible')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Usuarios vinculados
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {linkedUsers.length > 0 ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {linkedUsers.map((userEntry) => {
                  const userId = toSafeText(readByKeys(userEntry, ['id'], ''), '')
                  const names = toSafeText(readByKeys(userEntry, ['names', 'name'], ''), '')
                  const lastName = toSafeText(
                    readByKeys(userEntry, ['lastname_f', 'lastNameP'], ''),
                    ''
                  )
                  const label = toSafeText(
                    `${names} ${lastName}`.trim() || readByKeys(userEntry, ['email'], ''),
                    'Usuario'
                  )

                  return <Chip key={`${userId || label}`} variant="outlined" label={label} />
                })}
              </Stack>
            ) : (
              <Alert severity="info">{usersAvailable ? 'No hay usuarios vinculados para este producto.' : 'Usuarios vinculados: no disponible.'}</Alert>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resumen operativo
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2">Consumidores asociados: {consumersCount === null ? 'No disponible' : consumersCount}</Typography>
            <Typography variant="body2">Actividades recientes: {activitiesCount === null ? 'No disponible' : activitiesCount}</Typography>
            <Typography variant="body2">Usuarios vinculados: {usersAvailable ? linkedUsers.length : 'No disponible'}</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Información complementaria
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary">
              Este detalle presenta contexto operativo resumido del producto como unidad de negocio del cliente.
              No incluye analítica avanzada, configuración administrativa ni edición de layouts.
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </PrivatePageSection>
  )
}

export default ProductDetail
